import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The last thing between a render failure and a blank page.
 *
 * React unmounts the whole tree when an error escapes a render or an effect,
 * and what is left is the background colour - a black screen with nothing on
 * it, no message, and no clue about where to look. That is exactly what a
 * stale Phaser scene did here: it threw inside a bridge handler, the throw
 * travelled back into the effect that sent the message, and the application
 * disappeared. The cause is fixed and the bridge no longer carries a handler's
 * failure back to its sender, but neither of those makes this unnecessary.
 * There will be other errors, and none of them may be silent.
 *
 * This does not retry, hide or swallow anything: it says what broke, prints
 * the message, and offers a reload. The error still reaches the console.
 */

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly message: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { message: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.message === null) {
      return this.props.children;
    }
    return (
      <div className="app-error" data-testid="app-error" role="alert">
        <h1>Algo quebrou na interface.</h1>
        <p>
          A sessão continua no servidor: nada foi perdido, e recarregar retoma a caça de onde ela
          estava.
        </p>
        <code data-testid="app-error-message">{this.state.message}</code>
        <button type="button" data-testid="app-error-reload" onClick={() => location.reload()}>
          RECARREGAR
        </button>
      </div>
    );
  }
}
